// covered-by: packages/schemas/src/__tests__/knowledge-source-schema.spec.ts
import { z } from "zod";

import {
  KnowledgeScopeSource,
  KnowledgeSourceType,
  KnowledgeTrustLevel,
  knowledgeScopeSchema,
} from "./knowledge";

const timeStamps = z.object({
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export enum KnowledgeSourceStatus {
  UPLOADED = "uploaded",
  PROCESSING = "processing",
  READY = "ready",
  FAILED = "failed",
}

export enum KnowledgeExtractionStatus {
  PENDING = "pending",
  RUNNING = "running",
  SUCCEEDED = "succeeded",
  FAILED = "failed",
}

export const knowledgeSourceFileSchema = z.object({
  s3Key: z.string().min(1),
  fileName: z.string().min(1),
  mimeType: z.string().min(1),
  sizeBytes: z.coerce.number().int().min(1),
  sha256: z.string().optional(),
});

/** Rembrandt-written extraction lifecycle subdoc. */
export const knowledgeSourceExtractionSchema = z.object({
  status: z.nativeEnum(KnowledgeExtractionStatus),
  error: z.string().nullable().optional(),
  ruleCount: z.coerce.number().int().min(0).optional(),
  attempt: z.coerce.number().int().min(0).optional(),
  completedAt: z.coerce.date().nullable().optional(),
});

export const knowledgeSourceSchema = z
  .object({
    _id: z.string(),
    enterpriseId: z.string(),
    sourceType: z.nativeEnum(KnowledgeSourceType),
    trustLevel: z.nativeEnum(KnowledgeTrustLevel),
    file: knowledgeSourceFileSchema,
    status: z.nativeEnum(KnowledgeSourceStatus),
    error: z.string().nullable().optional(),
    description: z.string().nullable().optional(),
    scope: knowledgeScopeSchema.default({}),
    scopeSource: z
      .nativeEnum(KnowledgeScopeSource)
      .default(KnowledgeScopeSource.NONE),
    textStats: z
      .object({
        pageCount: z.coerce.number().int().min(0).optional(),
        sheetCount: z.coerce.number().int().min(0).optional(),
        charCount: z.coerce.number().int().min(0).optional(),
      })
      .optional(),
    extraction: knowledgeSourceExtractionSchema.optional(),
    // Reserved for the future interaction-memory pipeline. Declared NOW so
    // mongoose does not strip python-written values on hydration.
    interactionRef: z
      .object({
        kind: z.string().optional(),
        id: z.string().optional(),
      })
      .optional(),
    createdBy: z.string(),
    processedAt: z.coerce.date().nullable().optional(),
    deletedAt: z.coerce.date().nullable().optional(),
  })
  .merge(timeStamps);

export type KnowledgeSource = z.infer<typeof knowledgeSourceSchema>;
export type KnowledgeSourceFile = z.infer<typeof knowledgeSourceFileSchema>;
export type KnowledgeSourceExtraction = z.infer<
  typeof knowledgeSourceExtractionSchema
>;
